export interface JapaneseNumber {
    value: number;
    kanji: string;
    hiragana: string;
    romaji: string;
}

export type NumberConversion = { ok: true; result: JapaneseNumber } | { ok: false; error: "empty" | "invalid" | "range" };

type Reading = readonly [string, string];
const digits = ["零", "一", "二", "三", "四", "五", "六", "七", "八", "九"];
const readings: Reading[] = [["れい", "rei"], ["いち", "ichi"], ["に", "ni"], ["さん", "san"], ["よん", "yon"], ["ご", "go"], ["ろく", "roku"], ["なな", "nana"], ["はち", "hachi"], ["きゅう", "kyū"]];
const hundreds: Record<number, Reading> = { 3: ["さんびゃく", "sanbyaku"], 6: ["ろっぴゃく", "roppyaku"], 8: ["はっぴゃく", "happyaku"] };
const thousands: Record<number, Reading> = { 3: ["さんぜん", "sanzen"], 8: ["はっせん", "hassen"] };

// Converts a 0-9999 slice using the existing place-value loop. Shared by the
// top-level call for values under 10000 and by each 万/億 group of a larger
// number, since every such group is itself just a plain 4-digit reading.
// `romajiTokens` keeps each place-value word (せん/ひゃく/じゅう/ones) as its
// own entry rather than a single glued string, so the caller can join them
// with spaces - a digit and its own place suffix (はっぴゃく, にじゅう) stay
// glued together as one lexical word, since that's a single spoken unit, but
// different place values read as separate words for a learner sounding the
// number out (はっぴゃく にじゅう ご, not はっぴゃくにじゅうご run together).
function convertFourDigitGroup(value: number): JapaneseNumber & { romajiTokens: string[] } {
    let remaining = value;
    let kanji = "";
    let hiragana = "";
    const romajiTokens: string[] = [];
    for (const [place, symbol, kana, latin, irregular] of [
        [1000, "千", "せん", "sen", thousands],
        [100, "百", "ひゃく", "hyaku", hundreds],
        [10, "十", "じゅう", "jū", {}],
        [1, "", "", "", {}],
    ] as [number, string, string, string, Record<number, Reading>][]) {
        const digit = Math.floor(remaining / place);
        remaining %= place;
        if (!digit) continue;
        const prefix = digit === 1 && place > 1 ? "" : digits[digit];
        kanji += prefix + symbol;
        const reading = irregular[digit] || (digit === 1 && place > 1 ? [kana, latin] : [readings[digit][0] + kana, readings[digit][1] + latin]);
        hiragana += reading[0];
        romajiTokens.push(reading[1]);
    }
    return { value, kanji, hiragana, romaji: romajiTokens.join(" "), romajiTokens };
}

// 万 (10^4, man) and 億 (10^8, oku) never trigger the sokuon/rendaku shifts
// that 百 and 千 do: まん starts with a nasal and おく with a vowel, neither
// of which a geminate consonant can precede, so every group is just its
// plain four-digit reading plus the counter - except that, unlike 十/百/千,
// a bare "1" group keeps its いち/ichi prefix (一万 いちまん, not just まん).
// This intentionally stops at 億 and does not support 兆 (10^12): 兆 has
// irregular sokuon forms (一兆 いっちょう, 六兆 ろっちょう, 八兆 はっちょう,
// 十兆 じゅっちょう) that could not be verified with confidence here.
const myriadUnits: { place: number; symbol: string; kana: string; latin: string }[] = [
    { place: 1e8, symbol: "億", kana: "おく", latin: "oku" },
    { place: 1e4, symbol: "万", kana: "まん", latin: "man" },
];

// 12 digits is exactly 9999億9999万9999 (999,999,999,999) - the largest
// value expressible with 億/万 alone, one short of needing 兆.
export const MAX_SUPPORTED_DIGITS = 12;

// `maxDigits` lets a caller keep its own, narrower advertised range (e.g.
// NumbersPage.tsx still documents and tests 0-9,999, its original range)
// without capping every other caller - like the currency converter, which
// needs the full 億/万 extension - to the same limit.
export function convertJapaneseNumber(input: string, maxDigits: number = MAX_SUPPORTED_DIGITS): NumberConversion {
    const normalized = input.trim().replace(/[０-９]/g, digit => String.fromCharCode(digit.charCodeAt(0) - 0xfee0));
    if (!normalized) return { ok: false, error: "empty" };
    if (!/^\d+$/.test(normalized)) return { ok: false, error: "invalid" };
    const significant = normalized.replace(/^0+/, "") || "0";
    if (significant.length > Math.min(maxDigits, MAX_SUPPORTED_DIGITS)) return { ok: false, error: "range" };
    const value = Number(significant);
    if (value === 0) return { ok: true, result: { value, kanji: "零", hiragana: "れい", romaji: "rei" } };

    let remaining = value;
    let kanji = "";
    let hiragana = "";
    const romajiTokens: string[] = [];
    for (const { place, symbol, kana, latin } of myriadUnits) {
        const groupValue = Math.floor(remaining / place);
        remaining %= place;
        if (!groupValue) continue;
        const group = convertFourDigitGroup(groupValue);
        kanji += group.kanji + symbol;
        hiragana += group.hiragana + kana;
        // 五万 is one word, goman, not "go man" - the 万/億 unit glues onto
        // the group's last place-value word, same as a digit glues to its
        // own place suffix above; only the words before it stay separate.
        const tokens = [...group.romajiTokens];
        tokens[tokens.length - 1] += latin;
        romajiTokens.push(...tokens);
    }
    const lastGroup = convertFourDigitGroup(remaining);
    kanji += lastGroup.kanji;
    hiragana += lastGroup.hiragana;
    romajiTokens.push(...lastGroup.romajiTokens);

    return { ok: true, result: { value, kanji, hiragana, romaji: romajiTokens.join(" ") } };
}
